import {RsnButton} from '@raison/ui-kit';
import {useInfiniteQuery} from '@tanstack/react-query';
import React, {Fragment, useEffect, useMemo} from 'react';
import {useInView} from 'react-intersection-observer';
import {useLocation, useNavigate} from 'react-router';
import {
    createTransactionIdPath,
    TransactionSchema,
    TransactionsHistory,
    useTransactionIdParam,
    useTransactionsHistoryOptions,
} from '~/entities/transactions-history';
import {TransactionDetailsModal, TransactionsList} from '~/features/transitions-list';
import {groupOrdersFormattedMonth} from './selectGroupedOrders';
import scss from './Transactions.module.scss';

const isAnyFilterSet = (filters: TransactionsHistory.Filters) => {
    const {date, ...rest} = filters;
    return !!date || Object.values(rest).flat().length > 0;
};

export function Transactions() {
    const [filters, {resetAll: resetFilters}] = TransactionsHistory.useFilters();

    const {
        data: {orders = [], total: totalLength} = {},
        isLoading,
        isFetching,
        isError,
        fetchNextPage,
        refetch,
    } = useInfiniteQuery({
        ...useTransactionsHistoryOptions(filters),
        gcTime: 0,
    });
    const transactionId = useTransactionIdParam();
    const transactionFromList = useMemo(() => {
        return orders.find(order => order.id === transactionId);
    }, [transactionId, orders]);

    const {ref, inView} = useInView();

    useEffect(() => {
        if (isFetching || isError) {
            // critical to not fetchNextPage when infinite query is already fetching
            // there's a known and difficult to reproduce bug that causes transactions list
            // to not update after user makes new transaction
            return;
        }
        if (inView) {
            fetchNextPage();
        }
    }, [fetchNextPage, inView, isFetching]);

    const groupedOrders = groupOrdersFormattedMonth(orders);

    const withWrapper = (content: React.JSX.Element) => (
        <Fragment>
            {content}
            <TransactionDetailsModal transaction={transactionFromList} transactionId={transactionId} />
        </Fragment>
    );

    const navigate = useNavigate();
    const {search} = useLocation();

    const onOrderClick = (order: TransactionSchema) => {
        navigate(createTransactionIdPath(order.id, search));
    };

    return withWrapper(
        <TransactionsList
            orders={groupedOrders}
            containerRef={ref}
            currentLength={orders.length}
            totalLength={totalLength}
            hasFilters={isAnyFilterSet(filters)}
            isError={isError}
            isFetching={isFetching}
            isLoading={isLoading}
            refetch={refetch}
            onOrderClick={onOrderClick}
        >
            {{
                nothingFound: () => (
                    <Fragment>
                        <div className={scss.noTransactionText}>
                            Transactions with such filters <br /> not found
                        </div>
                        <RsnButton.Secondary onClick={resetFilters}>Clear filters</RsnButton.Secondary>
                    </Fragment>
                ),
                zeroActivity: () => <div className={scss.noTransactionText}>You haven’t made any transactions yet</div>,
            }}
        </TransactionsList>
    );
}
